import type { GeographicAddress } from '../../../generated/api/geographicAddress.js';
import type { MatchedAddress } from '../../../generated/api/matchedAddress.js';
import type { AddressSearchResult } from '../../AddressSearch/AddressSearch.js';

export interface ApartmentOption {
  addressId: string;
  displayText: string;
}

/**
 * Flatten a single TMForum `GeographicAddress` into a flat list of selectable apartment options,
 * pulling staircases / subUnits into `"<street> <nr> <subUnitNumber|subAddressNumber>"` labels.
 */
export const flattenApartments = (addr: GeographicAddress): ApartmentOption[] => {
  const options: ApartmentOption[] = [];
  if (!addr.geographicSubAddress?.length) {
    return options;
  }
  for (const sub of addr.geographicSubAddress) {
    if (sub.subUnit?.length) {
      for (const unit of sub.subUnit) {
        options.push({
          addressId: unit.id,
          displayText: `${addr.streetName} ${addr.streetNr} ${unit.subUnitNumber}`,
        });
      }
    } else {
      options.push({
        addressId: sub.id,
        displayText: `${addr.streetName} ${addr.streetNr} ${sub.subAddressNumber}`,
      });
    }
  }
  return options;
};

/**
 * Map the TMForum search response into street-level matched addresses.
 * Deduplicates by `"<streetName> <streetNr>"` (case-insensitive) and skips entries without street info.
 */
export const mapToStreetResults = (addresses: GeographicAddress[], zipCode: string): AddressSearchResult => {
  const seen = new Set<string>();
  const matchedAddresses: MatchedAddress[] = [];
  for (const addr of addresses) {
    if (!addr.streetName || !addr.streetNr) {
      continue;
    }
    const key = `${addr.streetName} ${addr.streetNr}`.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    matchedAddresses.push({
      addressId: addr.id,
      addressText: `${addr.streetName} ${addr.streetNr}`,
    });
  }
  return { matchedAddresses, postalCode: zipCode };
};

/**
 * Find the `GeographicAddress` that owns the given addressId, looking at the top-level id as well as
 * any nested subAddress / subUnit ids. Falls back to any search hit for the same postal code.
 */
export const findGeoAddressForMatch = (
  searchResults: GeographicAddress[],
  matchedAddressId: string | undefined,
  postalCode: string
): GeographicAddress | undefined => {
  if (matchedAddressId) {
    const exact = searchResults.find(
      a =>
        a.id === matchedAddressId ||
        a.geographicSubAddress?.some(
          sub => sub.id === matchedAddressId || sub.subUnit?.some(u => u.id === matchedAddressId)
        )
    );
    if (exact) {
      return exact;
    }
  }
  return searchResults.find(a => a.postcode === postalCode);
};

/** Resolve the post-office name (a.k.a. city) from a matched geographic address, falling back to ''. */
export const resolvePostOffice = (addr?: GeographicAddress): string => addr?.postOfficeName ?? addr?.city ?? '';
